import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Play, MapPin, Clock, CheckCircle, Loader2, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { loginRedirect } from "@/components/Layout";
import { Button } from "@/components/ui/button";

/**
 * Slice R · Admin dashboard for the POI densify sweep.
 *   /admin/places
 *   · List of last 10 runs with progress bars
 *   · Country picker → "Sweep" button that kicks off a new background run
 *   · City density heat-strip (top N by places count)
 * Auth-gated by `require_admin` on every backend endpoint used here.
 */

const COUNTRIES = [
  { slug: "india",       label: "🇮🇳 India",       cities: 20 },
  { slug: "vietnam",     label: "🇻🇳 Vietnam",     cities: 5  },
  { slug: "thailand",    label: "🇹🇭 Thailand",    cities: 5  },
  { slug: "indonesia",   label: "🇮🇩 Indonesia",   cities: 5  },
  { slug: "philippines", label: "🇵🇭 Philippines", cities: 5  },
];

function RunRow({ run }) {
  const pct = run.cities_total
    ? Math.round((run.cities_done / run.cities_total) * 100)
    : 0;
  const done = run.status === "done";
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-4" data-testid={`densify-run-${run.run_id}`}>
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-full grid place-items-center shrink-0"
             style={{ background: done ? "#d1fae5" : "#fef3c7" }}>
          {done
            ? <CheckCircle size={18} className="text-emerald-700" />
            : <Loader2 size={18} className="text-amber-700 animate-spin" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-stone-900 truncate">
            {(run.country || "india").toUpperCase()} · {run.cities_done}/{run.cities_total} cities
          </div>
          <div className="text-[11px] text-stone-500 flex items-center gap-1 truncate">
            <Clock size={10} /> {run.started_at?.slice(0, 19).replace("T", " ")}
            {" · run "}{run.run_id}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-lg font-extrabold text-stone-900 tabular-nums">{run.total_created ?? 0}</div>
          <div className="text-[10px] text-stone-500 uppercase tracking-wide">new places</div>
        </div>
      </div>
      <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
        <div className="h-full bg-emerald-500 transition-all"
             style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2 text-center text-[11px]">
        <div><span className="font-bold tabular-nums">{run.total_hits    ?? 0}</span> <span className="text-stone-500">total hits</span></div>
        <div><span className="font-bold tabular-nums">{run.total_created ?? 0}</span> <span className="text-stone-500">created</span></div>
        <div><span className="font-bold tabular-nums">{run.total_merged  ?? 0}</span> <span className="text-stone-500">merged</span></div>
      </div>
    </div>
  );
}

export default function AdminPlaces() {
  const { user } = useAuth();
  const [country, setCountry] = useState("india");
  const [runs, setRuns] = useState([]);
  const [density, setDensity] = useState([]);
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [firing, setFiring] = useState(false);
  const [digestFiring, setDigestFiring] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        api.get("/places/densify/runs", { params: { limit: 10 } }),
        api.get("/places/density-by-city"),
        api.get("/places/growth-digest/mine"),
      ]);
      setRuns(r1.data?.items || []);
      setDensity(r2.data?.items || []);
      setDigest(r3.data?.notif_id ? r3.data : null);
      setForbidden(false);
    } catch (e) {
      if (e?.response?.status === 403 || e?.response?.status === 401) {
        setForbidden(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    refresh();
    const iv = setInterval(refresh, 8000);   // live progress every 8 s
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const kickoff = async () => {
    setFiring(true);
    try {
      const r = await api.post("/places/densify", null, { params: { country } });
      toast.success(`Sweep started · run ${r.data?.run_id?.slice(0, 12)} · ${r.data?.cities} cities`);
      refresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Sweep failed to start";
      toast.error(typeof msg === "string" ? msg : "Sweep failed");
    } finally {
      setFiring(false);
    }
  };

  const runDigest = async () => {
    setDigestFiring(true);
    try {
      const r = await api.post("/places/growth-digest/run-now");
      toast.success(`Digest computed · ${r.data?.notified} admins notified · +${r.data?.added_week?.toLocaleString?.() || 0} places this week`);
      refresh();
    } catch (e) {
      toast.error("Digest failed to run");
    } finally {
      setDigestFiring(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-5 py-16 text-center">
        <h2 className="heading text-3xl font-extrabold mb-3">Admin only</h2>
        <Button onClick={loginRedirect} className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="admin-places-login">
          Login with Google
        </Button>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-16 text-center">
        <h2 className="heading text-3xl font-extrabold mb-3">Admins only</h2>
        <p className="text-stone-600">Your account isn't in the admin allow-list.
          Ask a Find2Near operator to grant access, then reload.</p>
      </div>
    );
  }

  const topDensity = density.slice(0, 12);
  const maxCount = Math.max(1, ...density.map(d => d.count));

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-8" data-testid="admin-places">
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">Admin · POI Densify</div>
          <h1 className="heading text-3xl md:text-4xl font-extrabold text-stone-900">Places Coverage</h1>
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          className="text-sm rounded-full border border-stone-200 hover:border-stone-300 px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="admin-places-refresh"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Weekly growth digest — auto-generated Monday 03:30 UTC + on-demand. */}
      <div className="mt-5 bg-gradient-to-br from-emerald-600 to-emerald-800 rounded-2xl p-4 text-white shadow-sm" data-testid="growth-digest-card">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-emerald-100">
          <TrendingUp size={12} /> This week's growth
          <button
            onClick={runDigest}
            disabled={digestFiring}
            className="ml-auto text-[10px] font-bold rounded-full bg-white/10 hover:bg-white/20 border border-white/25 px-2.5 py-1 inline-flex items-center gap-1 disabled:opacity-60"
            data-testid="growth-digest-run"
          >
            {digestFiring
              ? <Loader2 size={10} className="animate-spin" />
              : <RefreshCw size={10} />}
            Run now
          </button>
        </div>
        {digest ? (
          <>
            <div className="mt-1 text-lg font-black tabular-nums" data-testid="growth-digest-title">{digest.title}</div>
            <div className="text-[12px] text-emerald-100 mt-0.5">{digest.body}</div>
            {digest.meta?.top_sweeps?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {digest.meta.top_sweeps.slice(0, 3).map((s, i) => (
                  <span key={i} className="text-[10px] font-bold rounded-full bg-white/15 border border-white/20 px-2 py-0.5">
                    #{i + 1} {s.country || "india"} · +{(s.total_created || 0).toLocaleString()}
                  </span>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="mt-1 text-[12px] text-emerald-100">
            No digest yet — tap <b>Run now</b> to compute one, or wait for the Monday 03:30 UTC scheduler.
          </div>
        )}
      </div>

      {/* Kickoff strip */}
      <div className="mt-5 bg-white border border-stone-200 rounded-2xl p-4" data-testid="densify-kickoff">
        <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-2">
          Sweep another country
        </div>
        <div className="flex flex-wrap gap-2">
          {COUNTRIES.map(c => (
            <button
              key={c.slug}
              onClick={() => setCountry(c.slug)}
              className={`text-xs font-bold rounded-full px-3 py-1.5 border transition-colors ${
                country === c.slug
                  ? "bg-emerald-600 border-emerald-700 text-white"
                  : "bg-white border-stone-200 hover:border-stone-300 text-stone-700"
              }`}
              data-testid={`densify-country-${c.slug}`}
            >
              {c.label}<span className="opacity-70 ml-1">· {c.cities}</span>
            </button>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-2">
          <Button
            onClick={kickoff}
            disabled={firing}
            className="rounded-full bg-emerald-600 hover:bg-emerald-700 gap-2"
            data-testid="densify-fire"
          >
            {firing
              ? <Loader2 size={14} className="animate-spin" />
              : <Play size={14} />}
            Sweep {country}
          </Button>
          <div className="text-[11px] text-stone-500">
            Runs as a background task — poll below for progress.
          </div>
        </div>
      </div>

      {/* Density heat-strip */}
      <div className="mt-6" data-testid="density-heatstrip">
        <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-2">
          <MapPin size={11} className="inline -mt-0.5" /> City density · top 12 by places count
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {topDensity.length === 0 && !loading && (
            <div className="col-span-full text-sm text-stone-400 bg-white border border-stone-200 rounded-2xl p-6 text-center">
              No places yet. Kick off a sweep above.
            </div>
          )}
          {topDensity.map(d => {
            const pct = Math.round((d.count / maxCount) * 100);
            return (
              <div key={`${d.country}-${d.city}`}
                   className="bg-white border border-stone-200 rounded-xl p-3"
                   data-testid={`density-row-${d.city}`}>
                <div className="flex items-center gap-2">
                  <div className="text-sm font-bold text-stone-900 truncate flex-1">{d.city}</div>
                  <div className="text-sm font-extrabold text-emerald-700 tabular-nums">{d.count}</div>
                </div>
                <div className="text-[10px] uppercase tracking-wide text-stone-400">{d.country}</div>
                <div className="mt-1 h-1.5 rounded-full bg-stone-100 overflow-hidden">
                  <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Run history */}
      <div className="mt-6" data-testid="densify-history">
        <div className="flex items-center gap-2 mb-2">
          <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Recent sweeps</div>
          <Link to="/places" className="text-[11px] text-emerald-700 hover:text-emerald-800 font-semibold ml-auto">
            Open live map →
          </Link>
        </div>
        <div className="space-y-2">
          {runs.length === 0 && !loading && (
            <div className="text-sm text-stone-400 bg-white border border-stone-200 rounded-2xl p-6 text-center">
              No sweeps recorded yet.
            </div>
          )}
          {runs.map(r => <RunRow key={r.run_id} run={r} />)}
        </div>
      </div>
    </div>
  );
}
